import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Eye, EyeOff, Pencil, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/AdminGate";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMedia() {
  useSeo({ title: "Admin · Media Library | Cool Runnings Cape Town", noindex: true });
  return (
    <AdminGate>
      <MediaManager />
    </AdminGate>
  );
}

function MediaManager() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const loadBusy = useRef(false);

  const load = async () => {
    if (loadBusy.current) return;
    loadBusy.current = true;
    try {
      const res = await fetch(`${API}/admin/media`, { credentials: "include" });
      if (res.ok) setItems(await res.json());
    } catch {
      /* keep the current list on transient failures */
    } finally {
      loadBusy.current = false;
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async () => {
    if (!files || files.length === 0) return toast.error("Choose image or video files first");
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      try {
        const res = await fetch(`${API}/media/upload`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(`${file.name}: ${err.detail || "upload failed"}`);
        } else {
          ok += 1;
        }
      } catch {
        toast.error(`${file.name}: upload failed`);
      }
    }
    setBusy(false);
    setFiles(null);
    setTitle("");
    if (ok > 0) {
      toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded to the gallery`);
      load();
    }
  };

  const toggle = async (id) => {
    await fetch(`${API}/admin/media/${id}/toggle`, { method: "POST", credentials: "include" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this media from the gallery?")) return;
    await fetch(`${API}/admin/media/${id}/delete`, { method: "POST", credentials: "include" });
    toast.success("Removed");
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditBusy(true);
    try {
      const res = await fetch(`${API}/admin/media/${editing}/edit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editTitle }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Could not save the caption");
        return;
      }
      toast.success("Caption updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save the caption");
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <section className="pb-24 pt-10 sm:pt-14" data-testid="media-admin-page">
      <PageHeader
        tight
        pageName="Admin"
        eyebrow="Staff portal · gallery"
        title="GALLERY MEDIA"
        subline="Upload photos and videos for the public gallery — edit captions, hide or remove anything."
        image="/admin-header.jpg"
      />
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="card-dark mt-10 p-6" data-testid="media-upload-card">
          <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
            <div>
              <label className="text-xs font-extrabold uppercase tracking-widest text-[#b9b4a6]">Files (images or videos, max 60 MB)</label>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                onChange={(e) => setFiles(e.target.files)}
                className="input-dark mt-2 !py-2.5 file:mr-3 file:rounded-full file:border-0 file:bg-[#f8b818] file:px-3 file:py-1 file:text-xs file:font-extrabold"
                data-testid="media-upload-file-input"
              />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-widest text-[#b9b4a6]">Caption (optional)</label>
              <input
                className="input-dark mt-2"
                placeholder="e.g. School outings on the track"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="media-upload-title-input"
              />
            </div>
          </div>
          <button onClick={upload} disabled={busy} className="btn-gold mt-5 disabled:opacity-60" data-testid="media-upload-submit-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={15} />}
            {busy ? "Uploading…" : "Upload to gallery"}
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-admin-grid">
          {items.map((m) => (
            <div key={m.id} className={`card-dark overflow-hidden ${m.visible ? "" : "opacity-50"}`} data-testid={`media-admin-item-${m.id}`}>
              <div className="h-36 bg-black">
                {m.media_type === "video" ? (
                  <video src={m.src} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                ) : (
                  <img src={m.src} alt={m.title} className="h-full w-full object-cover" />
                )}
              </div>
              <div className="bg-[#fff7e8] p-3">
                <p className="min-h-[2.4rem] text-xs font-bold leading-snug text-[#241c0d]" data-testid={`media-caption-${m.id}`}>
                  {m.title || "No caption yet"}
                </p>
                <div className="mt-2 flex items-center justify-between gap-1">
                  <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">
                    <ImageIcon size={11} /> {m.media_type}
                  </span>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => {
                        setEditing(m.id);
                        setEditTitle(m.title || "");
                      }}
                      className="rounded-full border border-black/15 p-2 text-[#241c0d] transition-colors hover:border-[#e89b00] hover:bg-[#f8b818]"
                      aria-label="Edit caption"
                      data-testid={`media-edit-btn-${m.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => toggle(m.id)}
                      className="rounded-full border border-black/15 p-2 text-[#241c0d] transition-colors hover:border-[#e89b00] hover:text-[#e89b00]"
                      aria-label={m.visible ? "Hide" : "Show"}
                      data-testid={`media-toggle-btn-${m.id}`}
                    >
                      {m.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      className="rounded-full border border-black/15 p-2 text-[#241c0d] transition-colors hover:border-red-500 hover:text-red-500"
                      aria-label="Delete"
                      data-testid={`media-delete-btn-${m.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="!bg-[#fff7e8] !text-[#241c0d]" data-testid="media-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#241c0d]">EDIT CAPTION</DialogTitle>
            <DialogDescription className="!text-[#8a7b5e]">
              This is the writing underneath the thumbnail — visitors see it on the gallery.
            </DialogDescription>
          </DialogHeader>
          <input
            className="mt-2 w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-bold text-[#241c0d] outline-none focus:border-[#f8b818]"
            maxLength={80}
            placeholder="e.g. School outings on the track"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            data-testid="media-edit-input"
          />
          <button onClick={saveEdit} disabled={editBusy} className="btn-gold mt-2 w-full justify-center" data-testid="media-edit-save-btn">
            {editBusy ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={14} />}
            {editBusy ? "Saving…" : "Save caption"}
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}
